import {
  DELETE_APPOINTMENT,
  MockTicketClient,
  TicketClient,
  UPDATE_APPOINTMENT,
  useQuery
} from '@nile/graphql/src';
import React, { useState } from 'react';
import { GET_APPOINTMENT } from '@nile/graphql/src';
import {
  Avatar,
  Button,
  Modal,
  TextStyle,
  TextContainer
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  AttachmentPreviewer,
  DateTimeFormatter,
  EventEmitter,
  Loader,
  getParsedCrudData,
  getTranslatedText,
  toast,
  useIntl,
  getDownloadURL,
  useIsAuthorisedHook,
  getCurrentUserHook,
  Row
} from '@nile/components/src';
import AppointmentForm from './AppointmentForm';
import dompurify from 'dompurify';
import { useMutation } from '@apollo/client';
import { cloneDeep, isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';
import AttachmentHolder from '@nile/rmm/src/modules/common/components/sub-pages/attachment-holder';
import { dompurifier } from '@nile/components/src/Utils/helpers';

const AppointmentViewEditContainer = (props) => {
  const { eventId, isPrivateEvent } = props;
  const intl = useIntl();

  const getSVGIcon = (status) => {
    switch (status) {
      case 'ACCEPTED':
        return {
          name: 'FeatureAvailable',
          color: '#77B96D'
        };
      case 'DECLINED':
        return {
          name: 'CircleCancelMinor',
          color: '#ED7777'
        };
      case 'TENTATIVELY_ACCEPTED':
        return {
          name: 'Maybe'
        };
      default:
        return null;
    }
  };

  const [eventData, setEventData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [showAttachmentPreview, setShowAttachmentPreview] = useState({
    show: false,
    index: 0
  });
  const [formatterAttachment, setFormatterAttachment] = useState([]);

  let appointmentSource = eventData?.appointment?.appointmentSource || null;
  if (appointmentSource) {
    appointmentSource =
      appointmentSource?.charAt(0).toUpperCase() +
      appointmentSource.slice(1).toLowerCase();
  }

  const [deleteAppointment, { loading: deleteLoading }] = useMutation(
    DELETE_APPOINTMENT,
    {
      client: TicketClient,
      onCompleted() {
        EventEmitter.dispatch('refreshCalendar');
        // toast('success', 'scheduling.appointment.delete.success');
        setDeleteModalActive(false);
        if (props?.togglePopOver) {
          props?.togglePopOver();
        }
      }
    }
  );

  const [updateAppointment, { loading: updateLoading }] = useMutation(
    UPDATE_APPOINTMENT,
    {
      client: TicketClient,
      onCompleted() {
        EventEmitter.dispatch('refreshCalendar');
        // toast('success', 'scheduling.appointment.edit.success');
        if (props?.togglePopOver) {
          props?.togglePopOver();
        }
      }
    }
  );

  const closeDeleteModal = () => {
    setDeleteModalActive(false);
  };

  const handleDelete = () => {
    props?.event?.onDelete(eventData);
    deleteAppointment({
      variables: {
        input: {
          appointmentId: eventId
        }
      }
    });
  };

  const { loading, data: calendarEvents } = useQuery(GET_APPOINTMENT, {
    client: TicketClient,
    variables: {
      input: {
        appointment: {
          appointmentId: eventId
        },
        workItem: !isEmpty(props?.workItem) ? props?.workItem : null
      }
    },
    fetchPolicy: 'network-only',
    onCompleted(res) {
      setEventData(res?.getAppointment);
    }
  });

  const isDispatcher = useIsAuthorisedHook('dispatcherCalendar.allow');
  const currentUser = getCurrentUserHook();

  const getIsAppointmentEditDeleteAuthorised = () => {
    if (props?.viewMode) {
      return false;
    }

    if (eventData?.appointment?.syncStatus !== 'SUCCESS') {
      return false;
    }

    if (isDispatcher) {
      return true;
    } else {
      const organiser = eventData?.appointment?.attendees.find(
        (el) => el?.acceptanceStatus === 'ORGANIZER'
      );
      return organiser?.user?.userId === currentUser?.userId;
    }
  };

  const isAppointmentEditDeleteAuthorised = getIsAppointmentEditDeleteAuthorised();

  const attachments = !isEmpty(eventData?.appointment?.attachments)
    ? cloneDeep(eventData?.appointment?.attachments)
    : null;

  const getAttachmentURL = (item) => {
    const module = 'CALENDAR_EVENT_ATTACHMENT';
    const details = {
      appointmentId: eventData?.eventId,
      fileName: item?.fileName
    };

    const url = getDownloadURL({ module, details });
    return url;
  };

  const formatAttachmentForPreview = (attachments, cb) => {
    const formattedItems = attachments.map((item) => {
      if (item)
        return {
          name: item.fileName,
          url: getAttachmentURL(item),
          type: item.fileName.substr(
            item.fileName.lastIndexOf('.') + 1,
            item.fileName.length
          )
        };
    });
    setFormatterAttachment(formattedItems);
    cb();
  };

  const openAttachmentPreview = (index, attachments, item) => {
    if (attachments) {
      formatAttachmentForPreview(attachments, () => {
        setShowAttachmentPreview({
          ...showAttachmentPreview,
          show: true,
          index
        });
      });
    }
  };

  const closePreviwer = () => {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: false
    });
  };

  const formatAttendees = (user) => {
    // return user.map(({ id, ...keepAttrs }) => ({
    //   ...keepAttrs
    // }));
    return user.map((el) => ({
      user: {
        userId: el?.user?.userId || el?.id
      }
    }));
  };

  const onClose = () => {
    if (props?.togglePopOver) {
      props?.togglePopOver();
    }
  };

  const onSave = (data) => {
    const prevAttachments = eventData?.appointment?.attachments.map(
      ({ fileName, originalFileName, fileSize, ...dropAttrs }) => ({
        fileName,
        originalFileName,
        fileSize
      })
    );

    const currentAttachments = data?.attachments.map(
      ({ fileName, originalFileName, fileSize, ...dropAttrs }) => ({
        fileName,
        originalFileName,
        fileSize
      })
    );

    const prevAddedAttendees = eventData?.appointment?.attendees.map((el) => ({
      id: el?.user?.userId || el?.user?.email,
      acceptanceStatus: el?.acceptanceStatus,
      attendeeType: el?.attendeeType,
      attendeeId: el?.attendeeId,
      user: {
        userId: el?.user?.userId
      }
    }));

    const currentAttendees = data?.attendees.map((el) => ({
      id: el?.userId || el?.user?.userId || el?.user?.email,
      acceptanceStatus: el?.acceptanceStatus,
      attendeeType: el?.attendeeType,
      user: {
        userId: el?.userId || el?.user?.userId
      }
    }));

    let {
      addedItems: addAttendees,
      deletedItems: removeAttendees
    } = getParsedCrudData(prevAddedAttendees, currentAttendees, 'id');

    let {
      addedItems: addAttachments,
      deletedItems: deleteAttachments
    } = getParsedCrudData(prevAttachments, currentAttachments, 'fileName');

    const appointmentId = eventData?.eventId;
    const eventDuration = data?.eventDuration;
    const subject = data?.subject;
    const description = data?.description;
    const address = {
      addressId: data?.address?.addressId
    };

    const formatRemoveAttendees = removeAttendees.map((el) => ({
      attendeeId: el?.attendeeId
    }));

    updateAppointment({
      variables: {
        input: {
          appointmentId: appointmentId,
          eventDuration: eventDuration,
          subject: subject,
          description: description,
          address: !isEmpty(address) ? address : null,
          addAttendees: !isEmpty(addAttendees)
            ? formatAttendees(addAttendees)
            : [],

          removeAttendees: formatRemoveAttendees,
          addAttachments: addAttachments,
          deleteAttachments: deleteAttachments
        }
      }
    });
  };

  if (editMode) {
    return (
      <div className="relative">
        <AppointmentForm
          title={eventData?.appointment?.subject}
          start={eventData?.startTime}
          endTime={eventData?.endTime}
          workItem={eventData?.workItem}
          attendees={eventData?.appointment?.attendees?.map((el) => ({
            ...el,
            name: el?.user?.name,
            value: el?.user?.name,
            label: el?.user?.name,
            id: el?.user?.userId || el?.user?.id
          }))}
          address={{
            ...eventData?.appointment?.address,
            name: eventData?.appointment?.address?.name,
            label: eventData?.appointment?.address?.name,
            value: eventData?.appointment?.address?.value
          }}
          description={eventData?.appointment?.description}
          attachments={eventData?.appointment?.attachments}
          onCancel={() => setEditMode(false)}
          onSave={onSave}
          disableWorkitem
          saveLoading={updateLoading}
          minDate={props?.integratedDate}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        id="event-view-edit-container loading-state"
        className={`min-height-250 flex actr jctr position-relative p20 width-600 ${eventData
          ?.appointment?.appointmentSource !== 'INTERNAL' && 'pb40'}`}
      >
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div
        id="event-view-edit-container position-relative preview-state"
        className={`position-relative p20 pd-t0 width-600 ${eventData
          ?.appointment?.appointmentSource !== 'INTERNAL' && 'pb40'}`}
      >
        {isPrivateEvent && (
          <TextStyle variation="subdued">
            <Row gap={5} className="mb-10 items-center">
              <SVGIcon name="Lock" size="sm" />
              <FormattedMessage id="ticket.scheduling.privateEvent.title" />
            </Row>
          </TextStyle>
        )}
        <div className="flex fx-sb mb-20">
          <div className="f-20">
            <TextStyle weight="SemiBold">
              {eventData?.appointment?.subject || 'NA'}
            </TextStyle>
          </div>

          <div className="flex g10 actr">
            {eventData?.appointment?.appointmentSource === 'INTERNAL' &&
              isAppointmentEditDeleteAuthorised && (
                <div className="flex g10 actr">
                  <Button
                    plain
                    icon={<SVGIcon name="Edit" />}
                    onClick={() => setEditMode(true)}
                  />

                  <Modal
                    activator={
                      <Button
                        onClick={() => setDeleteModalActive(true)}
                        plain
                        icon={
                          <div className="mt4">
                            <SVGIcon name="Delete" />
                          </div>
                        }
                      />
                    }
                    open={deleteModalActive}
                    onClose={() => setDeleteModalActive(false)}
                    variant="confirmation"
                    title={getTranslatedText(intl, 'app.general.toast.warning')}
                    primaryAction={{
                      content: getTranslatedText(intl, 'common.confirm'),
                      onAction: handleDelete,
                      loading: deleteLoading
                    }}
                    secondaryActions={[
                      {
                        content: getTranslatedText(intl, 'common.cancel'),
                        onAction: closeDeleteModal
                      }
                    ]}
                  >
                    <Modal.Section>
                      <TextContainer>
                        <FormattedMessage id="event.delete.confirmation.message" />
                      </TextContainer>
                    </Modal.Section>
                  </Modal>
                </div>
              )}
            <Button plain icon={<SVGIcon name="Close" />} onClick={onClose} />
          </div>
        </div>

        <div className="flex g10 mb-15">
          <div>
            <SVGIcon name="DateTime" />
          </div>

          <div className="flex g5">
            <div>
              <DateTimeFormatter type="dateTime" date={eventData?.startTime} />
            </div>
            <div>-</div>
            <div>
              <DateTimeFormatter type="dateTime" date={eventData?.endTime} />
            </div>
          </div>
        </div>

        <div className="flex g10 mb-15">
          <div>
            <SVGIcon name="UserGroup" />
          </div>
          <div className="flex g10 max-height-50 wrap scroll w100">
            {eventData?.appointment?.attendees?.map((el) => {
              const initial = el?.user?.name?.[0];
              const iconDetails = getSVGIcon(el?.acceptanceStatus);
              return (
                <div className={`flex ${iconDetails ? 'g10' : 'g3'}`}>
                  <div className="appointment-attendee position-relative">
                    <Avatar initials={initial} size="extraSmall" />
                    {iconDetails && (
                      <div className="position-absolute appointment-attendee-status-icon">
                        <SVGIcon
                          name={iconDetails?.name}
                          color={iconDetails?.color}
                          size="xs"
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <TextStyle>{el?.user?.name || el?.user?.email}</TextStyle>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex g10 mb-15">
          <div>
            <SVGIcon name="Notes" />
          </div>
          <div className="max-height-150 scroll break-word w100">
            <div
              dangerouslySetInnerHTML={{
                __html: dompurifier(eventData?.appointment?.description)
              }}
            />
          </div>
        </div>

        <div className="flex g10 mb-15">
          <div>
            <SVGIcon name="Location" />
          </div>
          <div>
            <TextStyle>
              {eventData?.appointment?.address?.name || 'NA'}
            </TextStyle>
          </div>
        </div>

        {attachments && (
          <div className="flex wrap mt-10 g10 attachments-container mb-10">
            {attachments.map((item, index) => (
              <AttachmentHolder
                onClick={() => openAttachmentPreview(index, attachments, item)}
                fileName={item?.fileName || ''}
              />
            ))}
          </div>
        )}

        {showAttachmentPreview.show && (
          <AttachmentPreviewer
            showPreviewer={showAttachmentPreview.show}
            closePreviwer={closePreviwer}
            list={formatterAttachment}
            selectedIndex={showAttachmentPreview.index}
          />
        )}

        {eventData?.appointment?.appointmentSource !== 'INTERNAL' && (
          <div className="position-absolute mt-10">
            <TextStyle variation="subdued">
              <FormattedMessage id="scheduling.event.source.text" />
              <span className="capitalize">{` ${appointmentSource}`}</span>
            </TextStyle>
          </div>
        )}
      </div>
    </>
  );
};

export default AppointmentViewEditContainer;
